import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { brandLogoSvg } from '../../../components/brandLogo';
import { draw, travel } from '../../../components/svg';
import { words, revealWords, settleWords } from '../../../components/text';

const C = { x: 800, y: 450 };
const RX = 470;
const RY = 250;

interface Pillar {
  id: string;
  label: string;
  x: number;
  y: number;
  side: 'left' | 'right';
  from: number;
}

const PILLARS: Pillar[] = [
  { id: 'understand', label: 'UNDERSTAND', x: C.x - RX * Math.SQRT1_2, y: C.y - RY * Math.SQRT1_2, side: 'left', from: 1 },
  { id: 'build', label: 'BUILD', x: C.x + RX * Math.SQRT1_2, y: C.y - RY * Math.SQRT1_2, side: 'right', from: 1 },
  { id: 'connect', label: 'CONNECT', x: C.x + RX * Math.SQRT1_2, y: C.y + RY * Math.SQRT1_2, side: 'right', from: 1 },
  { id: 'evolve', label: 'EVOLVE', x: C.x - RX * Math.SQRT1_2, y: C.y + RY * Math.SQRT1_2, side: 'left', from: 2 },
];

const arc = (a: Pillar, b: Pillar): string => `M${a.x} ${a.y} A${RX} ${RY} 0 0 1 ${b.x} ${b.y}`;
const LOOP = `M${PILLARS[0].x} ${PILLARS[0].y}` + PILLARS.map((_, i) => {
  const b = PILLARS[(i + 1) % PILLARS.length];
  return ` A${RX} ${RY} 0 0 1 ${b.x} ${b.y}`;
}).join('');

/**
 * 0 Moderta at the centre, calm after the chaos
 * 1 Understand → Build → Connect: a line
 * 2 Evolve closes the line into a loop
 * 3 Purpose-built products for real-world problems.
 */
export class ApproachScene extends BaseScene {
  public readonly stepCount = 4;

  private spin?: gsap.core.Tween;
  private traveler?: gsap.core.Tween;

  protected template(): string {
    const spokes = PILLARS.map((p) => {
      const angle = Math.atan2(p.y - C.y, p.x - C.x);
      const sx = C.x + Math.cos(angle) * 118;
      const sy = C.y + Math.sin(angle) * 118;
      return `<path class="spoke draw" data-from="${p.from}" data-pillar="${p.id}" pathLength="1" d="M${sx.toFixed(1)} ${sy.toFixed(1)} L${p.x.toFixed(1)} ${p.y.toFixed(1)}"/>`;
    }).join('');

    const arcs = PILLARS.map((a, i) => {
      const b = PILLARS[(i + 1) % PILLARS.length];
      const from = Math.max(a.from, b.from);
      return `<path class="arc draw" data-from="${from}" data-arc="${i}" pathLength="1" d="${arc(a, b)}"/>`;
    }).join('');

    const nodes = PILLARS.map((p, i) => {
      const anchor = p.side === 'left' ? 'end' : 'start';
      const dx = p.side === 'left' ? -34 : 34;
      return `<g class="pillar" data-from="${p.from}" data-pillar="${p.id}">
        <g transform="translate(${p.x.toFixed(1)} ${p.y.toFixed(1)})">
          <circle class="pillar__halo" r="20"/>
          <circle class="pillar__dot" r="7"/>
          <g class="pillar__text">
            <text class="pillar__index" x="${dx}" y="-40" text-anchor="${anchor}">0${i + 1}</text>
            <text class="pillar__label" x="${dx}" y="16" text-anchor="${anchor}">${p.label}</text>
          </g>
        </g>
      </g>`;
    }).join('');

    return `
      <div class="scene-layout approach">
        <svg class="diagram approach-diagram" viewBox="0 0 1600 900" role="img" aria-label="Moderta at the centre of Understand, Build, Connect and Evolve">
          <g class="approach-lines">${spokes}${arcs}</g>
          <g class="core">
            <circle class="core__spin" cx="${C.x}" cy="${C.y}" r="112"/>
            <circle class="core__ring" cx="${C.x}" cy="${C.y}" r="92"/>
            ${brandLogoSvg('moderta', { x: C.x, y: C.y, height: 30, surface: 'dark', className: 'core__label' })}
          </g>
          ${nodes}
          <path class="approach-loop" d="${LOOP}"/>
          <g class="traveler" data-from="2"><circle r="5"/><circle class="traveler__glow" r="14"/></g>
        </svg>
        <p class="approach-statement line-statement" data-from="3">${words('Purpose-built products for real-world problems.')}</p>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    this.ensureSpin();
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    tl.fromTo(this.$('.core'), { opacity: 0, scale: 0.7, svgOrigin: `${C.x} ${C.y}` }, { opacity: 1, scale: 1, duration: 1.4, ease: 'expo.out' }, 0.15);
  }

  protected onExit(): void {
    this.spin = undefined;
    this.traveler = undefined;
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode('approach', step, immediate);
    this.ensureSpin();

    const forward = !immediate && step > previousStep;
    const tl = this.timeline();
    gsap.set(this.$('.core'), { opacity: 1, scale: 1, svgOrigin: `${C.x} ${C.y}` });

    // Paths: anything visible at this step is drawn; animate only what this step introduces.
    this.$$<SVGPathElement>('.draw').forEach((path) => {
      const from = Number(path.dataset.from);
      const introduced = forward && from === step;
      if (!introduced) draw(path, step >= from, { immediate: true });
    });

    gsap.set(this.$$('.pillar__dot, .pillar__halo'), { scale: 1, transformOrigin: '50% 50%' });
    gsap.set(this.$$('.pillar__text'), { opacity: 1, x: 0 });
    settleWords(this.$('.approach-statement'));

    if (forward && step === 1) this.introducePillars(tl, ['understand', 'build', 'connect'], [0, 1]);
    if (forward && step === 2) this.introducePillars(tl, ['evolve'], [2, 3]);
    if (forward && step === 3) tl.add(revealWords(this.$('.approach-statement'), { stagger: 0.05 }), 0.1);

    // The diagram steps back when the statement lands.
    gsap.to(this.$('.approach-diagram'), { opacity: step === 3 ? 0.55 : 1, y: step === 3 ? -24 : 0, duration: immediate ? 0 : 0.9, ease: 'power2.inOut' });

    // Once the loop is closed, a pulse travels it continuously.
    if (step >= 2 && !this.traveler) {
      const marker = this.$<SVGGElement>('.traveler');
      this.traveler = this.loop(travel(marker, this.$<SVGPathElement>('.approach-loop'), 7, forward && step === 2 ? 1.6 : 0));
    } else if (step < 2 && this.traveler) {
      this.traveler.kill();
      this.traveler = undefined;
    }
  }

  private introducePillars(tl: gsap.core.Timeline, ids: string[], arcs: number[]): void {
    ids.forEach((id, i) => {
      const at = i * 0.55;
      const spoke = this.$<SVGPathElement>(`.spoke[data-pillar="${id}"]`);
      const pillar = PILLARS.find((p) => p.id === id)!;
      const dots = this.$$(`.pillar[data-pillar="${id}"] circle`);
      const text = this.$(`.pillar[data-pillar="${id}"] .pillar__text`);
      draw(spoke, false, { immediate: true });
      tl.add(draw(spoke, true, { duration: 0.6, ease: 'power2.out' }), at);
      tl.fromTo(dots, { scale: 0 }, { scale: 1, transformOrigin: '50% 50%', duration: 0.7, ease: 'back.out(2.2)' }, at + 0.45);
      tl.fromTo(text, { opacity: 0, x: pillar.side === 'left' ? 18 : -18 }, { opacity: 1, x: 0, duration: 0.8, ease: 'power3.out' }, at + 0.55);
    });
    const arcStart = ids.length * 0.55 + 0.3;
    arcs.forEach((index, i) => {
      const path = this.$<SVGPathElement>(`.arc[data-arc="${index}"]`);
      draw(path, false, { immediate: true });
      tl.add(draw(path, true, { duration: 0.9, ease: 'power2.inOut' }), arcStart + i * 0.7);
    });
  }

  private ensureSpin(): void {
    if (this.spin) return;
    this.spin = this.loop(gsap.to(this.$('.core__spin'), { rotation: '+=360', svgOrigin: `${C.x} ${C.y}`, duration: 60, ease: 'none', repeat: -1 }));
  }
}
